import type { AuthenticatedUser, Role } from '../types/api';

// Coarse check: does the user hold this role *anywhere* - mirrors the
// backend's RolesGuard (@Roles() decorator), used to decide what nav items
// and routes to show. Fine-grained "for this specific org/site" checks
// still happen server-side on every write; the frontend only needs to
// decide what to render.
export function hasRole(user: AuthenticatedUser | null, ...roles: Role[]): boolean {
  if (!user) return false;
  if (user.isMspAdmin) return true;
  const held = new Set<Role>([...user.orgRoles.map((r) => r.role), ...user.siteRoles.map((r) => r.role)]);
  return roles.some((r) => held.has(r));
}

export function isCustomerAdminFor(user: AuthenticatedUser | null, orgId: string): boolean {
  if (!user) return false;
  if (user.isMspAdmin) return true;
  return user.orgRoles.some((r) => r.customerOrganisationId === orgId && r.role === 'CUSTOMER_ADMIN');
}

// Reports Viewer is read-only (equipment reports & cost summaries) - a
// separate check from isCustomerAdminFor since it does *not* imply full
// management access, unlike Customer Admin.
export function isReportsViewerFor(user: AuthenticatedUser | null, orgId: string): boolean {
  if (!user) return false;
  if (user.isMspAdmin) return true;
  return user.orgRoles.some((r) => r.customerOrganisationId === orgId && r.role === 'REPORTS_VIEWER');
}

// The single org a Customer Admin manages - used for the "My Organisation"
// shortcut nav item. Returns null for MSP Admin (who has no single "home"
// org) or a user with no CUSTOMER_ADMIN grant.
export function primaryCustomerAdminOrgId(user: AuthenticatedUser | null): string | null {
  if (!user || user.isMspAdmin) return null;
  const grant = user.orgRoles.find((r) => r.role === 'CUSTOMER_ADMIN');
  return grant?.customerOrganisationId ?? null;
}

// The org a customer-side user belongs to, regardless of which role grants
// it - Customer Admin (org-level) or Signing Authority/End User (site-
// level). Used for co-branding, which should show for everyone logging
// into "their part of the portal", not just admins. Null for MSP Admin.
export function primaryOrgId(user: AuthenticatedUser | null): string | null {
  if (!user || user.isMspAdmin) return null;
  if (user.orgRoles[0]) return user.orgRoles[0].customerOrganisationId;
  if (user.siteRoles[0]) return user.siteRoles[0].customerOrganisationId;
  return null;
}

export const ROLE_LABELS: Record<Role, string> = {
  MSP_ADMIN: 'MSP Admin',
  CUSTOMER_ADMIN: 'Customer Admin',
  REPORTS_VIEWER: 'Reports Viewer',
  SIGNING_AUTHORITY: 'Signing Authority',
  END_USER: 'End User',
};
